import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { ShieldCheck } from 'lucide-react';
import { TextInput } from '@/components/form/TextInput.tsx';
import InputHint from '@/components/form/base/InputHint.tsx';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip.tsx';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import { KnownBackendApiError } from '@/lib/api/exception.ts';
import useGetCompanyInfo from '@/features/dashboard/company/hooks/useGetCompanyInfo.ts';
import useSetEmailSenderSettings from '@/features/dashboard/emailSender/hooks/useSetEmailSenderSettings.ts';
import {
  emailSenderSchema,
  EmailSenderFormData,
} from '@/features/dashboard/emailSender/schemas/emailSenderSchema.ts';
import { EmailSenderSettingsDto } from '@/features/dashboard/emailSender/types/EmailSenderSettingsDto.ts';
import { emailSenderSettingsMapper } from '@/features/dashboard/emailSender/types/EmailSenderSettingsDto.mapper.ts';
import { EmailSettingStatus } from '@/features/dashboard/emailSender/types/EmailSettingStatus.ts';
import {
  emailSettingStatusTones,
  getEmailSettingStatusLabel,
} from '@/features/dashboard/emailSender/types/EmailSettingStatus.utils.ts';
import StatusDot from '@/components/ui/StatusDot.tsx';
import timeUtils from '@/utils/timeUtils.ts';

interface EditEmailSenderModalProps {
  currentSettings: EmailSenderSettingsDto;
  onClose: () => void;
}

const ApprovalRequiredLabel = ({ label }: { label: string }) => (
  <div className="flex items-center gap-1.5">
    <InputLabel label={label} />
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-default">
          <ShieldCheck size={14} className="text-neutral-400" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[280px]">
        Ze względów bezpieczeństwa (ochrona przed podszywaniem się pod cudze
        adresy) zmianę tego pola zatwierdza administrator Campdesk.
      </TooltipContent>
    </Tooltip>
  </div>
);

const SavedValueStatus = ({
  value,
  status,
  approvedAt,
}: {
  value: string | null;
  status: EmailSettingStatus | null;
  approvedAt: string | null;
}) => {
  if (!value || !status) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs leading-[14px] text-neutral-500">
      <span>Zapisany adres: {value}</span>
      <StatusDot
        tone={emailSettingStatusTones[status]}
        label={getEmailSettingStatusLabel(status)}
        className="gap-1.5"
      />
      {status === EmailSettingStatus.APPROVED && approvedAt && (
        <span>od {timeUtils.toDateTimeString(approvedAt)}</span>
      )}
    </div>
  );
};

const FORM_FIELD_NAMES: (keyof EmailSenderFormData)[] = [
  'fromEmail',
  'fromName',
  'replyToEmail',
];

const EditEmailSenderModal = ({
  currentSettings,
  onClose,
}: EditEmailSenderModalProps) => {
  const setMutation = useSetEmailSenderSettings();
  const { confirm } = confirmDialogUseHook();
  const { data: companyInfo } = useGetCompanyInfo();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<EmailSenderFormData>({
    resolver: zodResolver(emailSenderSchema),
    defaultValues: emailSenderSettingsMapper.fromDtoToForm(currentSettings),
  });

  const onSubmit = async (data: EmailSenderFormData) => {
    const request = emailSenderSettingsMapper.fromFormToDto(data);

    const changedApprovedFields: string[] = [];
    const revocationEffects: string[] = [];
    if (
      currentSettings.fromEmailStatus === EmailSettingStatus.APPROVED &&
      request.fromEmail !== currentSettings.fromEmail
    ) {
      changedApprovedFields.push('adres nadawcy');
      revocationEffects.push('maile będą wychodzić z domeny Campdesk');
    }
    if (
      currentSettings.replyToStatus === EmailSettingStatus.APPROVED &&
      request.replyToEmail !== currentSettings.replyToEmail
    ) {
      changedApprovedFields.push('adres Reply-To');
      revocationEffects.push('odpowiedzi będą trafiać na adres nadawcy');
    }

    if (changedApprovedFields.length > 0) {
      const confirmed = await confirm({
        title: 'Zmiana zatwierdzonej wartości',
        description: `Zmieniasz zatwierdzoną wartość: ${changedApprovedFields.join(' oraz ')}. Po zapisie zmiana wymaga ponownego zatwierdzenia — do tego czasu ${revocationEffects.join(', a ')}. Czy chcesz kontynuować?`,
        confirmText: 'Zapisz zmiany',
      });
      if (!confirmed) {
        return;
      }
    }

    setMutation.mutate(data, {
      onSuccess: () => onClose(),
      onError: (error) => {
        if (error instanceof KnownBackendApiError) {
          Object.values(error.getApiErrorDto()).forEach((singleError) => {
            const fieldName =
              singleError.fieldName as keyof EmailSenderFormData;
            if (FORM_FIELD_NAMES.includes(fieldName)) {
              setError(fieldName, { message: singleError.errorMessage });
            }
          });
        }
      },
    });
  };

  return (
    <Modal onClose={onClose} className="max-w-[560px]">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader title="Edytuj nadawcę e-mail" onClose={onClose} />
        <ModalContent>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <ApprovalRequiredLabel label="Adres nadawcy" />
              <TextInput
                register={register('fromEmail')}
                placeholder="rezerwacje@twojadomena.pl"
                error={errors.fromEmail?.message}
              />
              <div className="flex flex-col gap-0.5">
                <SavedValueStatus
                  value={currentSettings.fromEmail}
                  status={currentSettings.fromEmailStatus}
                  approvedAt={currentSettings.fromEmailApprovedAt}
                />
                <InputHint hint="Adres e-mail, z którego będą wysyłane wiadomości do uczestników. Zostaw puste, aby wysyłać z adresu Campdesk. Wymaga zatwierdzenia! Po zapisaniu skontaktujemy się w sprawie aktywacji." />
              </div>
            </div>
            <TextInput
              label="Nazwa nadawcy"
              register={register('fromName')}
              placeholder={companyInfo?.companyName ?? 'Nazwa firmy'}
              error={errors.fromName?.message}
              hint="Nazwa, którą uczestnicy zobaczą jako nadawcę wiadomości. Zostaw puste, aby używać nazwy firmy."
            />
            <div className="space-y-1.5">
              <ApprovalRequiredLabel label="Adres Reply-To" />
              <TextInput
                register={register('replyToEmail')}
                placeholder="biuro@twojadomena.pl"
                error={errors.replyToEmail?.message}
              />
              <div className="flex flex-col gap-0.5">
                <SavedValueStatus
                  value={currentSettings.replyToEmail}
                  status={currentSettings.replyToStatus}
                  approvedAt={currentSettings.replyToApprovedAt}
                />
                <InputHint hint="Adres e-mail, na który trafi wiadomość, gdy uczestnik kliknie „Odpowiedz” na otrzymany mail. Zostaw puste, aby odpowiedzi trafiały na adres nadawcy. Wymaga zatwierdzenia!" />
              </div>
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <ActionButton tone="ghost" size="sm" type="button" onClick={onClose}>
            Anuluj
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={setMutation.isPending}
          >
            Zapisz
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditEmailSenderModal;
